import type { PartialPropsOf } from "react";
import { forwardRef, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentCoverImage } from "~/components/content/content-cover-image";
import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import { checkIsMainTopic } from "~/components/content/helpers";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import { sanitizeReadonlyArray } from "~/lib/utils";
import type { magazinesStyleCardContentItemContent$key } from "~/queries/__generated__/magazinesStyleCardContentItemContent.graphql";

import { Container, Cover, CoverEntityLink, Headline, StyledEntityLink, Topic } from "./styles";

export type CoverImageAspectRatio = "1/1" | "3/2";
const COVER_IMAGE_VARIANTS: Record<
  CoverImageAspectRatio,
  PartialPropsOf<typeof ContentCoverImage>
> = {
  "1/1": {
    responsiveVariants: {
      desktopUp: "size1200x1200",
      mobileUp: "size500x500",
      tabletUp: "size500x500",
    },
  },
  "3/2": {
    responsiveVariants: {
      desktopUp: "size1200x800",
      mobileUp: "size540x360",
      tabletUp: "size540x360",
    },
  },
};

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: magazinesStyleCardContentItemContent$key;
};
const newContentItem = ({ ratio }: { ratio: CoverImageAspectRatio }) => {
  const coverImageVariants = COVER_IMAGE_VARIANTS[ratio];

  // React node
  const ContentItemComponent = forwardRef<HTMLDivElement, Props>(
    ({ className, provider, reference }, elementReference) => {
      const article = useFragment(
        graphql`
          fragment magazinesStyleCardContentItemContent on Content {
            topics {
              entityId
              ...topicLinkTopic
            }
            ...entityLink
            ...helpersCheckIsMainTopicContent
          }
        `,
        reference,
      );

      const cover = (
        <CoverEntityLink reference={article}>
          {provider.coverImage(coverImageVariants)}
        </CoverEntityLink>
      );
      const mainTopic = useMemo(() => {
        const topics = sanitizeReadonlyArray(article.topics);
        return topics.find(t => checkIsMainTopic(article, t.entityId));
      }, [article]);

      const headline = (
        <StyledEntityLink reference={article}>
          {provider.headline({ preferSocialHeadline: true, variant: MagazinesStyleVariant })}
        </StyledEntityLink>
      );

      return (
        <Container className={className} ref={elementReference}>
          <Cover>{cover}</Cover>
          {mainTopic ? <Topic reference={mainTopic} /> : null}
          <Headline>{headline}</Headline>
        </Container>
      );
    },
  );

  ContentItemComponent.displayName = {
    "1/1": "ContentItemMagazinesStyleCardSquare",
    "3/2": "ContentItemMagazinesStyleCardWide",
  }[ratio];
  return ContentItemComponent;
};

export const ContentItemMagazinesStyleCardSquare = newContentItem({ ratio: "1/1" });
export const ContentItemMagazinesStyleCardWide = newContentItem({ ratio: "3/2" });
